import { Content, Header, Page } from '@backstage/core-components';
import {
  DevHubBox,
  DevHubButton,
  DevHubGrid,
  DevHubStep,
  DevHubStepContent,
  DevHubStepLabel,
  DevHubStepper,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { useState } from 'react';
import AppBulkEditReview from './AppBulkEditReview';
import AppBulkEditSelectFields from './AppBulkEditSelectFields';
import AppBulkEditViewApps from './AppBulkEditViewApps';
import AppBulkEditAddNewValue from './AppBulkEditAddNewValue';
import { AppBulkEditProvider } from './context/AppBulkEditContext';

enum Step {
  ViewSelectedApplication = 'View Selected Application',
  SelectApplicationField = 'Select Application Field',
  AddNewValue = 'Add New Value',
  Review = 'Review',
}

export type ValidationType = {
  hasError: boolean;
  errorMessage: string;
};

/**
 * Component for bulk editing applications.
 * @returns JSX.Element The AppBulkEdit component.
 */
const AppBulkEdit = (): JSX.Element => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [validation, setValidation] = useState<ValidationType>({
    hasError: false,
    errorMessage: '',
  });
  const [toggleSubmit, setToggleSubmit] = useState<boolean>(false);
  const [toggleReset, setToggleReset] = useState<boolean>(false);

  const [steps] = useState<Step[]>([
    Step.ViewSelectedApplication,
    Step.SelectApplicationField,
    Step.AddNewValue,
    Step.Review,
  ]);

  const VALIDATION_RESET_DELAY = 100;

  function getStepContent(step: Step) {
    switch (step) {
      case Step.ViewSelectedApplication:
        return (
          <AppBulkEditViewApps
            setValidationHasError={setValidation}
            toggleReset={toggleReset}
          />
        );
      case Step.SelectApplicationField:
        return (
          <AppBulkEditSelectFields setValidationHasError={setValidation} />
        );
      case Step.AddNewValue:
        return <AppBulkEditAddNewValue setValidationHasError={setValidation} />;
      case Step.Review:
        return <AppBulkEditReview toggleSubmit={toggleSubmit} />;
      default:
        return 'Unknown step';
    }
  }

  const handleReset = () => {
    setActiveStep(0);
    setToggleSubmit(false);
    setTimeout(() => {
      setValidation({
        hasError: false,
        errorMessage: '',
      });
    }, VALIDATION_RESET_DELAY);
    setToggleReset(prev => !prev);
  };

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
    setValidation({
      hasError: false,
      errorMessage: '',
    });
    setToggleReset(false);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleSubmit = () => {
    setToggleSubmit(true);
  };

  const handleStartAnother = () => {
    setToggleReset(true);
    handleReset();
  };

  return (
    <AppBulkEditProvider>
      <Page themeId="home">
        <Header
          title="Applications Bulk Edit"
          subtitle="Bulk edit Applications in DevHub"
        />
        <Content>
          <DevHubBox>
            <DevHubStepper activeStep={activeStep} orientation="vertical">
              {steps.map(label => (
                <DevHubStep key={label}>
                  <DevHubStepLabel>{label}</DevHubStepLabel>
                  <DevHubStepContent>
                    <DevHubBox>{getStepContent(label)}</DevHubBox>
                    <DevHubBox>
                      <>
                        {validation && (
                          <DevHubGrid container padding="small">
                            <DevHubTypography paragraph variant="subtitle1">
                              {validation?.errorMessage}
                            </DevHubTypography>
                          </DevHubGrid>
                        )}
                        <DevHubGrid container direction="row">
                          {!toggleSubmit && (
                            <DevHubButton
                              onClick={handleReset}
                              disabled={activeStep === 0}
                            >
                              Reset
                            </DevHubButton>
                          )}
                          {activeStep === steps.length - 1 && toggleSubmit && (
                            <DevHubButton
                              onClick={handleStartAnother}
                              disabled={activeStep === 0}
                            >
                              Start Another Edit
                            </DevHubButton>
                          )}
                          {!toggleSubmit && (
                            <DevHubButton
                              disabled={activeStep === 0}
                              onClick={handleBack}
                            >
                              Back
                            </DevHubButton>
                          )}
                          {activeStep !== steps.length - 1 && (
                            <DevHubButton
                              variant="contained"
                              color="primary"
                              disabled={validation?.hasError}
                              onClick={handleNext}
                            >
                              Next
                            </DevHubButton>
                          )}
                          {activeStep === steps.length - 1 && (
                            <DevHubButton
                              variant="contained"
                              color="primary"
                              disabled={validation?.hasError || toggleSubmit}
                              onClick={handleSubmit}
                            >
                              Submit
                            </DevHubButton>
                          )}
                        </DevHubGrid>
                      </>
                    </DevHubBox>
                  </DevHubStepContent>
                </DevHubStep>
              ))}
            </DevHubStepper>
          </DevHubBox>
        </Content>
      </Page>
    </AppBulkEditProvider>
  );
};
export default AppBulkEdit;
